import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { MessageCircle } from 'lucide-react'
import { brand, contact, getCategory, getSubCategory } from '@/content/site'
import { productImagesFor } from '@/content/products'
import { colors, layout } from '@/theme'
import type { Product } from '@/content/types'
import { Section } from '@/components/ui/Section'
import { Breadcrumbs, type Crumb } from '@/components/ui/Breadcrumbs'
import { ProductGallery } from '@/components/product/ProductGallery'
import { SpecTable } from '@/components/product/SpecTable'
import { RelatedProducts } from '@/components/product/RelatedProducts'
import { Seo } from '@/components/seo/Seo'
import { JsonLd } from '@/components/seo/JsonLd'

/** Data-driven product detail view (rendered by the category/segment dispatcher). */
export function ProductDetailView({ product }: { product: Product }) {
  const cat = getCategory(product.category)
  const sub = product.subCategory ? getSubCategory(product.category, product.subCategory) : undefined
  const [colorIdx, setColorIdx] = useState(0)
  const activeColor = product.colors?.[colorIdx]
  const images = activeColor ? productImagesFor(activeColor.sku) : product.gallery
  const path = `/products/${product.category}/${product.slug}`

  const crumbs: Crumb[] = [
    { label: 'Home', to: '/' },
    { label: 'Products', to: '/products' },
    ...(cat ? [{ label: cat.name, to: `/products/${cat.slug}` }] : []),
    ...(sub ? [{ label: sub.name, to: `/products/${product.category}/${sub.slug}` }] : []),
    { label: product.name },
  ]

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    sku: product.modelNumber,
    mpn: product.modelNumber,
    category: cat?.name,
    description: product.description,
    brand: { '@type': 'Brand', name: brand.name },
    image: images.map((img) => `${brand.url}${img.src}`),
  }

  const whatsappHref = contact.whatsapp
    ? `${contact.whatsapp}?text=${encodeURIComponent(`Inquiry about ${product.modelNumber} — ${product.name}`)}`
    : '/contact'

  const seoTitle = product.seo?.title ?? `${product.name} (${product.modelNumber})`
  const seoDescription = product.seo?.description ?? product.shortDescription

  return (
    <>
      <Seo title={seoTitle} path={path} description={seoDescription} image={images[0]?.src ?? brand.logoBadge} />
      <JsonLd data={productSchema} />

      <Section sx={{ pb: { xs: 6, md: 8 } }}>
        <Breadcrumbs items={crumbs} />

        <Grid container spacing={{ xs: 4, md: 8 }}>
          {/* Gallery */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ position: { md: 'sticky' }, top: `${layout.headerHeight + 24}px` }}>
              <ProductGallery key={activeColor?.sku ?? 'default'} images={images} alt={`${product.name} (${product.modelNumber})`} />
            </Box>
          </Grid>

          {/* Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="overline" sx={{ color: 'primary.main' }}>
              {cat?.name}
              {sub ? ` · ${sub.name}` : ''}
            </Typography>
            <Typography variant="h2" component="h1" sx={{ mt: 1 }}>
              {product.name}
            </Typography>
            <Typography variant="subtitle2" component="p" sx={{ color: 'text.secondary', mt: 1.5 }}>
              Model {product.modelNumber}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3 }}>
              {product.description}
            </Typography>

            {product.colors && product.colors.length > 0 && (
              <Box sx={{ mt: 3.5 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1.25 }}>
                  Colour{activeColor ? ` — ${activeColor.name}` : ''}
                </Typography>
                <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {product.colors.map((c, i) => (
                    <Box
                      key={c.sku}
                      component="button"
                      type="button"
                      onClick={() => setColorIdx(i)}
                      aria-label={c.name}
                      aria-current={i === colorIdx}
                      title={c.name}
                      sx={{
                        p: 0,
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        bgcolor: c.swatch,
                        border: `2px solid ${i === colorIdx ? colors.gold : colors.border}`,
                        boxShadow: i === colorIdx ? `0 0 0 2px ${colors.bg}, 0 0 0 3px ${colors.gold}` : 'none',
                        transition: 'border-color 200ms ease, box-shadow 200ms ease',
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box sx={{ mt: 4, borderTop: `1px solid ${colors.border}` }}>
              <SpecTable product={product} />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
              <Button variant="contained" component={RouterLink} to="/contact">
                Send Inquiry
              </Button>
              <Button
                variant="outlined"
                component="a"
                href={whatsappHref}
                {...(contact.whatsapp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                startIcon={<MessageCircle size={18} />}
              >
                Enquire on WhatsApp
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Section>

      <Section sx={{ pt: 0 }}>
        <RelatedProducts product={product} />
      </Section>
    </>
  )
}
